/**
 * Token Storage Abstraction Layer
 *
 * Centralizes all auth token storage operations. Supports two modes:
 * - "Remember Me" ON:  tokens stored in localStorage (persists across tabs/browser restarts)
 * - "Remember Me" OFF: tokens stored in sessionStorage (current behavior, per-tab isolation)
 *
 * On read, checks both storages (localStorage first) for graceful cross-tab access.
 */

const REMEMBER_ME_KEY = 'rememberMe';

/**
 * Check if "Remember Me" is enabled.
 * The flag itself always lives in localStorage (must survive tab close).
 */
export function isRememberMe() {
  return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
}

/**
 * Set the "Remember Me" preference.
 * When switching from remembered to not-remembered, migrates tokens to sessionStorage.
 */
export function setRememberMe(value) {
  const prev = isRememberMe();
  localStorage.setItem(REMEMBER_ME_KEY, value ? 'true' : 'false');

  if (prev && !value) {
    // Migrating from localStorage to sessionStorage
    const keys = [
      'token', 'refreshToken', 'user', 'organizations', 'currentOrganization',
      'roles', 'permissions', 'maxRoleLevel', 'isImpersonating', 'originalUser',
      'originalToken', 'originalRefreshToken', 'impersonatedBy',
    ];
    keys.forEach((key) => {
      const val = localStorage.getItem(key);
      if (val !== null) {
        sessionStorage.setItem(key, val);
        localStorage.removeItem(key);
      }
    });
  }
}

/**
 * Get the active storage backend based on "Remember Me" preference.
 */
function getStorage() {
  return isRememberMe() ? localStorage : sessionStorage;
}

/**
 * Get an item from storage.
 * Checks localStorage first (for cross-tab access), then sessionStorage.
 */
export function getItem(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

/**
 * Set an item in the active storage backend.
 */
export function setItem(key, value) {
  getStorage().setItem(key, value);
}

/**
 * Remove an item from both storages.
 */
export function removeItem(key) {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

/**
 * Clear all auth-related data from both storages.
 */
export function clearAll() {
  const keys = [
    'token', 'refreshToken', 'user', 'organizations', 'currentOrganization',
    'roles', 'permissions', 'maxRoleLevel', 'isImpersonating', 'originalUser',
    'originalToken', 'originalRefreshToken', 'impersonatedBy',
  ];
  keys.forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  localStorage.removeItem(REMEMBER_ME_KEY);
}

// Convenience accessors for tokens
export function getToken() {
  return getItem('token');
}

export function setToken(value) {
  setItem('token', value);
}

export function getRefreshToken() {
  return getItem('refreshToken');
}

export function setRefreshToken(value) {
  setItem('refreshToken', value);
}
